import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import styles from "../styles/loginContainer.module.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import LoginLoading from "./LoginLoading";
import { loginAPI } from "../loginAPI";

export const ResetPassword = observer(() => {
  const { t } = useTranslation("login");
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetStatus, setResetStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (resetStatus !== "success") {
      return undefined;
    }

    const timer = setTimeout(() => {
      navigate("/login");
    }, 2000);

    return () => clearTimeout(timer);
  }, [navigate, resetStatus]);

  const handleResetPassword = async () => {
    if (!password || !confirmPassword) {
      setErrorMessage(t("Please fill in all fields"));
      return;
    }
    if (password.length < 8) {
      setErrorMessage(t("Password must be more than 8 characters"));
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage(t("Passwords do not match"));
      return;
    }
    if (!token) {
      setErrorMessage(t("Invalid or missing token"));
      return;
    }

    setErrorMessage("");
    setResetStatus("processing");
    try {
      await loginAPI.resetPassword(token, password, confirmPassword);
      setResetStatus("success");
    } catch (error) {
      console.error(error);
      setResetStatus("idle");
    }
  };

  if (resetStatus === "processing") {
    return (
      <LoginLoading
        message={t("Resetting password...")}
        onBack={() => setResetStatus("idle")}
      />
    );
  }

  if (resetStatus === "success") {
    return (
      <LoginLoading
        status="success"
        message={t("Password reset successfully. Redirecting to login...")}
        onBack={() => navigate("/login")}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.resetMain}>
        <div className={styles.resetCard}>
          <div className={styles.logo}>
            <img height={60} src="/NXMap-white-blue.png" alt="NXMap Logo" />
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleResetPassword();
            }}
            className={styles.resetForm}
          >
            <label>
              {t("New Password")}
              <input
                className={styles.passwordInput}
                type={showPassword ? "text" : "password"}
                placeholder={t("New Password")}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrorMessage("");
                }}
              />
              <span
                className={styles.passwordToggle}
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </span>
            </label>

            <label>
              {t("Confirm Password")}
              <input
                className={styles.passwordInput}
                type={showConfirmPassword ? "text" : "password"}
                placeholder={t("Confirm Password")}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setErrorMessage("");
                }}
              />
              <span
                className={styles.passwordToggle}
                onClick={() => setShowConfirmPassword((prev) => !prev)}
              >
                {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </span>
            </label>

            {errorMessage ? (
              <div className={styles.resetError}>{errorMessage}</div>
            ) : null}

            <button type="submit" className={styles.loginBtn}>
              {t("Reset Password")}
            </button>
          </form>

          <p
            className={styles.resetBackLink}
            onClick={() => navigate("/login")}
          >
            {t("Back to Login")}
          </p>
        </div>
      </div>
    </div>
  );
});

export default ResetPassword;
